/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import {
  BuilderPermission,
  CMDatesFilter,
  JobListFilterDto,
  JobRestService,
  MasterZoneRestService,
  OfficeRestService,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { AppRouter, DictionaryService, getRouteQueryParams, InitViewService } from '@gh/common-shared';
import { Enum, SlicedDataSource } from '@gh/core-data';
import { ColumnEnableConditions, GridColumn, GridLayoutService } from '@gh/core-ui-grid';

import { JobListItem } from './cm.model';
import { BuilderUserContext, FilterNames, FilterRestService } from '@gh/builder-shared';
import { Observable } from 'rxjs';
import { MasterZone } from '../../builder-shared/switch-to/resources/master-zone.model';
import { ID } from '@gh/common-shared-data';
import { Office } from '../../builder-shared/switch-to/resources/office.model';
import { ENV_BASE_URL } from '@gh/config';
import { padStart } from 'lodash';
import { sortByStringField, toString } from '@gh/core-util';
import { CMJobRestService } from './cm-job-rest.service';
import { map$, switchMap$ } from '@gh/rx/operators';
import { combineLatest$, of$ } from '@gh/rx';

@Injectable()
export class CmListBusinessService implements InitViewService {

  COLUMN_ENABLE_CONDITION = {
    totalAmount:              (): boolean => this.getCountry() !== 'us',
    sitePostcode:             (): boolean => this.getCountry() !== 'nz',
    masterZoneName:           (): boolean => this.userContext.corporateView,
    estimateCost:             (): boolean => this.hasCostPermission(),
    totalMasterFee:           (): boolean => this.userContext.corporateView && this.getCountry() !== 'us',
    totalMasterVariationFee: (): boolean => this.userContext.corporateView && this.getCountry() !== 'us',
    masterPaymentDue:         (): boolean => this.userContext.corporateView && this.getCountry() !== 'us',
  } as ColumnEnableConditions;

  constructor(
    private jobRest: JobRestService,
    private cMJobRest: CMJobRestService,
    private userContext: BuilderUserContext,
    private masterZoneRest: MasterZoneRestService,
    private officeRest: OfficeRestService,
    private dictionaries: DictionaryService,
    private gridLayoutService: GridLayoutService,
    private filterService: FilterRestService,
    private router: AppRouter,
  ) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const masterZone = this.userContext.masterZone?.id || this.getMasterZoneIdFromRoute(route);
    const filterName = this.userContext.corporateView ? FilterNames.cmList : FilterNames.cmListMaster;
    return this.filterService.get(filterName)
      .pipe(switchMap$((filter: any) => combineLatest$([
        this.gridLayoutService.get('cmList', this.COLUMN_ENABLE_CONDITION),
        this.dictionaries.getByEnum(CMDatesFilter),
        this.dictionaries.get('JOBS_FILTERS'),
        this.userContext.corporateView ? this.findAllMasterZones() : of$([this.userContext.masterZone]),
        masterZone || filter.masterZoneId ? this.findAllOfficesByMaster(masterZone || filter.masterZoneId) : of$([]),
      ]).pipe(map$(([
        gridLayout,
        cMDatesFilter,
        jobFilters,
        masterZones,
        offices,
                                                            ]) => {
        if (filter.officeId) {
          if (!offices.find((office) => office.id === filter.officeId)) {
            filter.officeId = '';
          }
        }
        return {
          gridLayout,
          cMDatesFilter,
          jobFilters,
          masterZones,
          offices,
          filter,
          filterName,
        };
      }))));
  }

  findAllMasterZones(): Observable<MasterZone[]> {
    return this.masterZoneRest.findAll();
  }

  findAllAsDataSource(filter: JobListFilterDto): SlicedDataSource<JobListItem> {
    return this.cMJobRest.findAllCmAsDataSource(filter);
  }

  findAllOfficesByMaster(masterId: ID, name?: string): Observable<Office[]> {
    return this.officeRest.findAllByMasterZone(masterId, ['deactivated', 'active', 'blocked'])
      .pipe(map$((res) => name ? sortByStringField(res.filter((office) => office.name.toLowerCase()
          .includes(name.toLowerCase())),          'status')
        : [{id: '', name: 'All'} as Office, ...sortByStringField(res, 'status')]));
  }

  getCountry(): string {
    const baseUrl = ENV_BASE_URL.toString();
    // @ts-ignore
    switch (true) {
      case baseUrl.includes('-au') || baseUrl.includes('.au'):
        return 'au';
      case baseUrl.includes('-nz') || baseUrl.includes('.nz'):
        return 'nz';
      case baseUrl.includes('-us') || baseUrl.includes('.com'):
        return 'us';
      default:
        return 'us';
    }
  }

  hasCostPermission(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.EDIT)
      || this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.VIEW);
  }

  export(filter: JobListFilterDto, columns: GridColumn[]): Observable<{ url: string; fileName: string }> {
    if (filter.dateFrom) {
      const dateFrom = new Date(filter.dateFrom);
      filter.dateFrom = `${ [dateFrom.getFullYear(), dateFrom.getMonth() + 1, dateFrom.getDate()].map(this.stringifyDateComponent)
        .join('-') }`;
    }
    if (filter.dateTo) {
      const dateTo = new Date(filter.dateTo);
      filter.dateTo = `${ [dateTo.getFullYear(), dateTo.getMonth() + 1, dateTo.getDate()].map(this.stringifyDateComponent).join('-') }`;
    }
    if (filter.dateType) {
      filter.dateType = Enum.toValue(CMDatesFilter, filter.dateType);
    }
    return this.jobRest.exportContactManagerList(filter, columns);
  }

  recalculate(): Observable<any> {
    return this.jobRest.recalculateJobList();
  }

  validate(jobId: ID): Observable<any> {
    return this.jobRest.validateCM(jobId);
  }

  stringifyDateComponent(n: number): string {
    return padStart(toString(n), 2, '0'); // tslint:disable-line:no-magic-numbers
  }

  onJobBillingOpen(job: JobListItem, title: string): void {
    this.router.navigateToChild(['/contracts-manager/office', job.officeId, 'billing-info', job.id], {
      parentTitle: title,
    });
  }
  private getMasterZoneIdFromRoute(route: ActivatedRouteSnapshot): string {
    const params = getRouteQueryParams(route);
    if (params && params.filter) {
      const filter = JSON.parse(params.filter);
      if (filter && filter.masterZoneId) {
        return filter.masterZoneId;
      }
    }
    return '';
  }
  private getOfficeIdFromRoute(route: ActivatedRouteSnapshot): string {
    const params = getRouteQueryParams(route);
    if (params && params.filter) {
      const filter = JSON.parse(params.filter);
      if (filter && filter.officeId) {
        return filter.officeId;
      }
    }
    return '';
  }
}
